import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  IProcessedMaterial,
  ManufacturingHistoryWithDetails,
  ManufacturingRecipeWithDetails,
  CreateManufacturingHistoryDto,
  CreateProcessedMaterialDto,
  UpdateProcessedMaterialDto,
} from 'shared';
import apiClient from '../api/client';

function normalizeManufacturingHistory(
  payload: unknown,
): ManufacturingHistoryWithDetails[] {
  if (Array.isArray(payload)) {
    return payload;
  }
  if (
    payload &&
    typeof payload === 'object' &&
    Array.isArray((payload as { history?: unknown }).history)
  ) {
    return (payload as { history: ManufacturingHistoryWithDetails[] }).history;
  }
  return [];
}

interface ProcessedMaterialDetail extends IProcessedMaterial {
  manufacturingRecipes?: ManufacturingRecipeWithDetails[];
}

export function useProcessedMaterials() {
  const queryClient = useQueryClient();

  const processedMaterialsQuery = useQuery({
    queryKey: ['processed-materials'],
    queryFn: async () => {
      const { data } = await apiClient.get<
        IProcessedMaterial[] | { materials?: IProcessedMaterial[] }
      >('/processed-materials');
      if (Array.isArray(data)) {
        return data;
      }
      if (data && Array.isArray(data.materials)) {
        return data.materials;
      }
      return [];
    },
  });

  const manufacturingHistoryQuery = useQuery({
    queryKey: ['manufacturing-history'],
    queryFn: async () => {
      const { data } = await apiClient.get<
        ManufacturingHistoryWithDetails[] | { history: ManufacturingHistoryWithDetails[] }
      >(
        '/manufacturing-history',
      );
      return normalizeManufacturingHistory(data);
    },
  });

  const manufactureMutation = useMutation({
    mutationFn: (dto: CreateManufacturingHistoryDto) =>
      apiClient.post('/manufacturing-history', dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['processed-materials'] });
      queryClient.invalidateQueries({ queryKey: ['manufacturing-history'] });
    },
  });

  const updateHistoryMutation = useMutation({
    mutationFn: ({ id, quantityProduced, manufacturedAt }: { id: string; quantityProduced: number; manufacturedAt?: string }) =>
      apiClient.put(`/manufacturing-history/${id}`, { quantityProduced, manufacturedAt }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['processed-materials'] });
      queryClient.invalidateQueries({ queryKey: ['manufacturing-history'] });
    },
  });

  const deleteHistoryMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/manufacturing-history/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['processed-materials'] });
      queryClient.invalidateQueries({ queryKey: ['manufacturing-history'] });
    },
  });

  const createProcessedMaterialMutation = useMutation({
    mutationFn: (dto: CreateProcessedMaterialDto) =>
      apiClient.post('/processed-materials', dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['processed-materials'] });
    },
  });

  const updateProcessedMaterialMutation = useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdateProcessedMaterialDto }) =>
      apiClient.put(`/processed-materials/${id}`, dto),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['processed-materials'] });
      if (variables?.id) {
        queryClient.invalidateQueries({ queryKey: ['processed-material', variables.id] });
      }
    },
  });

  const deleteProcessedMaterialMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/processed-materials/${id}`),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ['processed-materials'] });
      if (id) {
        queryClient.removeQueries({ queryKey: ['processed-material', id] });
        queryClient.invalidateQueries({ queryKey: ['manufacturing-history'] });
      }
    },
  });

  return {
    processedMaterials: processedMaterialsQuery.data ?? [],
    processedMaterialsLoading: processedMaterialsQuery.isLoading,
    manufacturingHistory: manufacturingHistoryQuery.data ?? [],
    manufacturingHistoryLoading: manufacturingHistoryQuery.isLoading,
    manufactureMutation,
    updateHistoryMutation,
    deleteHistoryMutation,
    createProcessedMaterialMutation,
    updateProcessedMaterialMutation,
    deleteProcessedMaterialMutation,
  };
}

export function useProcessedMaterialDetail(id?: string) {
  return useQuery({
    queryKey: ['processed-material', id],
    queryFn: async () => {
      const { data } = await apiClient.get<ProcessedMaterialDetail>(`/processed-materials/${id}`);
      return data;
    },
    enabled: !!id,
  });
}

export function useManufacturingHistoryForMaterial(processedMaterialId?: string) {
  return useQuery({
    queryKey: ['manufacturing-history', processedMaterialId],
    queryFn: async () => {
      const { data } = await apiClient.get<
        ManufacturingHistoryWithDetails[] | { history: ManufacturingHistoryWithDetails[] }
      >(
        '/manufacturing-history',
      );
      const history = normalizeManufacturingHistory(data);
      if (!processedMaterialId) {
        return history;
      }
      return history.filter((entry) => entry.processedMaterialId === processedMaterialId);
    },
  });
}
